import { AfterViewInit, Component, HostBinding, HostListener, Input, OnChanges, SimpleChanges } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { takeUntil } from 'rxjs/operators';
import { asEmptyAttribute, EmptyAttribute, InputBooleanAttribute, isTruthyInput } from '../core/empty-attribute';
import { FormElementCore } from '../core/form-element-core';

/**
 * Switch Component
 * Switches toggle the selection of an item on or off
 *
 * Switches are similar to checkboxes, and can be unselected or selected, using `formControlName`, `ngModel` or `checked` (not recommended).
 * @example
 * <campus-switch
 *  [label]="Switch Label"
 *  [supportingText]="Switch SupportingText">
 *  [icons]="true"
 *  [showOnlySelectedIcon]="false"
 *  [(ngModel)]="switchControl"
 * </campus-switch>
 *
 * @example
 * <form [formGroup]="form">
 *  <campus-switch
 *    [label]="Switch Label"
 *    formControlName="switchControl"
 *  </campus-switch>
 * </form>
 *
 * @documentation https://docs.kabas.be/components/switch
 *
 * @see A11Y: Switch Pattern:  https://www.w3.org/WAI/ARIA/apg/patterns/switch/
 *
 */

@Component({
  standalone: false,
  selector: 'campus-switch',
  templateUrl: './switch.component.html',
  styleUrls: ['./switch.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: SwitchComponent,
      multi: true,
    },
  ],
})
export class SwitchComponent extends FormElementCore<boolean> implements OnChanges, AfterViewInit {
  private _withIcons: boolean;
  private _showOnlySelectedIcon: boolean;

  //#region public fields

  /**
   * This enables the switch to be used with [checked]="false|true" or as checked attribute.
   * @example <campus-switch [checked]="true"></campus-switch>
   * @example <campus-switch checked></campus-switch>
   */
  @Input()
  set checked(value: InputBooleanAttribute) {
    this.value = isTruthyInput(value);
  }

  @Input()
  get withIcons() {
    return this._withIcons;
  }
  set withIcons(value: InputBooleanAttribute) {
    this._withIcons = isTruthyInput(value);
  }

  @Input()
  get showOnlySelectedIcon() {
    return this._showOnlySelectedIcon;
  }
  set showOnlySelectedIcon(value: InputBooleanAttribute) {
    this._showOnlySelectedIcon = isTruthyInput(value);
  }

  @Input() unselectedIcon: string;
  @Input() selectedIcon: string;

  @Input() target: 'wrapper' | 'none' | undefined = 'wrapper';
  //#endregion

  //#region getters and setters
  @HostBinding('attr.role') role = 'switch';

  @HostBinding('attr.aria-checked')
  ariaChecked: boolean;

  @HostBinding('attr.tabindex')
  tabIndex = 0;

  @HostBinding('class')
  defaultClasses = [
    'switch',
    'inline-flex',
    'gap-2xs',
    'items-center',
    'max-w-fit',
    'outline-none',
    'cursor-pointer',

    'group',
  ];

  public pressing = false;
  public focused = false;

  public viewInitialized = false;

  get currentValue() {
    return this.formControl?.value ?? this.value;
  }

  @HostBinding('attr.data-has-icons')
  hasIcons: EmptyAttribute;

  @HostBinding('attr.data-checked')
  attrChecked: EmptyAttribute;

  //#endregion

  //#region public methods
  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    if (event.code === 'Space' || event.code === 'Enter') {
      event.preventDefault();
      this.toggle();
    }
  }

  @HostListener('focusin')
  onFocusIn() {
    this.focused = true;
  }

  @HostListener('focusout')
  onFocusOut() {
    this.focused = false;
    this.handleBlur();
  }

  @HostListener('mousedown', ['$event'])
  onMouseDown(event: MouseEvent) {
    event.preventDefault();
    this.pressing = true;
  }

  @HostListener('mouseup')
  onMouseUp() {
    this.toggle();
    this.pressing = false;
  }

  toggle() {
    this.handleBlur();
    this.handleInput({ target: { checked: !this.currentValue } });
  }

  ngAfterViewInit(): void {
    this.viewInitialized = true;

    this.formControl?.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(() => this.onValueChanged());

    this._cd.detectChanges();
  }

  ngOnChanges(changes: SimpleChanges): void {
    super.ngOnChanges(changes);

    if (changes.withIcons || changes.showOnlySelectedIcon || changes.value) {
      this.hasIcons = asEmptyAttribute(this.showOnlySelectedIcon ? this.currentValue : this.withIcons);
    }
    if (changes.disabled) {
      this.tabIndex = isTruthyInput(changes.disabled.currentValue) ? -1 : 0;
    }
  }

  handleInput(event) {
    const value = (event.target as HTMLInputElement).checked;
    if (this.onChange) {
      this.onChange(value);
    } else {
      this.value = value;
    }
  }
  //#endregion

  protected onValueChanged(): void {
    this.ariaChecked = this.currentValue;
    this.attrChecked = asEmptyAttribute(this.currentValue);
    this.hasIcons = asEmptyAttribute(this.showOnlySelectedIcon ? this.currentValue : this.withIcons);
  }
}
